import { SyntheticEvent, useState } from 'react';

import SearchIcon from '@mui/icons-material/Search';
import {
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  FilterOptionsState,
  InputAdornment,
  TextFieldProps,
} from '@mui/material';
import MUIAutocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';

import NoOptionsText from 'UI/components/autocomplete/NoOptionsText.tsx';
import { useT } from 'UI/lib';

import RenderSuggestion from './RenderSuggestion';
import useStyles from './styles';
import { DEFAULT_SEARCH_DEBOUNCE } from 'CONSTANTS';
import { useDebouncedCallback } from 'use-debounce';

interface AutocompleteOption {
  __typename?: string;
  name?: string;
  synonymString?: string;
}

interface AutocompleteProps<T extends AutocompleteOption> {
  id?: string;
  loading?: boolean;
  items: T[];
  placeholder?: string;
  dataTest?: string;
  label?: string;
  onSearch: (value: string) => void;
  onSelect: (option: T) => void;
  inputIconClass?: string;
  size?: TextFieldProps['size'] | 'large';
  delay?: number;
  autoFocus?: boolean;
  clearOnBlur?: boolean;
  autoSelect?: boolean;
  disableClearable?: boolean;
  clearInputOnSelect?: boolean;
  onClear?: () => void;
  selectedItem?: any;
  disabled?: boolean;
  isOptionEqualToValue?: (option: T, value: any) => boolean;
  variant?: 'standard' | 'outlined' | 'filled';
  getOptionLabel?: (option: T) => string;
}

const Autocomplete = <T extends AutocompleteOption>({
  id = '',
  loading = false,
  items = [],
  placeholder,
  dataTest,
  label,
  onSearch,
  onSelect,
  inputIconClass,
  size = 'medium',
  delay = DEFAULT_SEARCH_DEBOUNCE,
  autoFocus = false,
  clearOnBlur = false,
  autoSelect = false,
  disableClearable = false,
  clearInputOnSelect = true,
  onClear,
  selectedItem = null,
  disabled = false,
  isOptionEqualToValue = (option, value) => option === value,
  variant = 'standard',
  getOptionLabel,
}: AutocompleteProps<T>) => {
  const classes = useStyles();
  const t = useT();

  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isAutocompleteOpened, setIsAutocompleteOpened] = useState(false);

  const debouncedHandleSearch = useDebouncedCallback((value: string) => {
    onSearch(value);
    setIsTyping(false);
  }, delay);

  const onChange = (_: SyntheticEvent, option: T | null, reason: AutocompleteChangeReason) => {
    debouncedHandleSearch.cancel();

    if (reason === 'clear') {
      onClear?.();
      return;
    }

    if (option) {
      onSelect(option);
      if (clearInputOnSelect) {
        setInputValue('');
      }
    }
  };

  const onInputChange = (
    _: SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => {
    setInputValue(value);
    const trimmedInputValue = value.trim();

    if (!!trimmedInputValue && reason !== 'reset') {
      setIsTyping(true);
      debouncedHandleSearch(trimmedInputValue.toLowerCase());
    }
  };

  const defaultGetOptionLabel = (option: T): string => {
    const value =
      option.__typename === 'JobSynonym' || option.__typename === 'CompanyOpportunity'
        ? option?.name
        : option?.synonymString;

    return value ?? '';
  };

  const resolveOptionLabel = (option: T): string =>
    (getOptionLabel ?? defaultGetOptionLabel)(option) ?? '';

  /*
  This prevents the options to be show while loading - means we show the loading text
  Also hide options if the input is empty, this is done to hide options from previous search.
  The issue with previous search is related to Global search being in global context
   */
  const handleFilterOptions = (options: T[], state: FilterOptionsState<T>): T[] => {
    if (loading || !state.inputValue.trim()) {
      return [];
    }

    return options;
  };

  // Let MUI handle default open/close (select, blur, etc.) but force closed in specific cases
  const shouldForceClose = !inputValue.trim() || (isTyping && items.length === 0);
  const isOpen = shouldForceClose ? false : isAutocompleteOpened;

  return (
    <MUIAutocomplete
      value={selectedItem}
      inputValue={inputValue}
      id={id}
      fullWidth
      open={isOpen}
      onOpen={() => setIsAutocompleteOpened(true)}
      onClose={() => setIsAutocompleteOpened(false)}
      autoSelect={autoSelect}
      clearOnBlur={clearOnBlur}
      blurOnSelect={true}
      disableClearable={disableClearable}
      noOptionsText={<NoOptionsText search={inputValue} />}
      loading={loading}
      loadingText={`${t('common:loading')}...`}
      options={items}
      onChange={onChange}
      getOptionLabel={resolveOptionLabel}
      onInputChange={onInputChange}
      disabled={disabled}
      slotProps={{
        paper: {
          'data-test': 'component-autocomplete-items',
        },
        clearIndicator: {
          sx: {
            visibility: 'visible', //Make the clear indicator always visible
          },
        },
      }}
      filterOptions={handleFilterOptions}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={params => {
        const { InputProps, ...restParams } = params;
        return (
          <TextField
            {...(restParams as any)}
            autoFocus={autoFocus}
            variant={variant}
            data-test={dataTest}
            label={label}
            placeholder={placeholder}
            //There is no 'large' size for TextField, so we use 'medium' and apply custom styles to make it bigger
            size={size === 'large' ? 'medium' : size}
            fullWidth
            InputProps={{
              ...InputProps,
              disableUnderline: size === 'large',
              startAdornment: size !== 'large' && (
                <InputAdornment
                  data-test="search-input"
                  position="start"
                  classes={{ root: inputIconClass }}
                >
                  <SearchIcon data-test="search-icon" />
                </InputAdornment>
              ),
              classes: {
                input: size === 'large' ? classes.bigInput : classes.basicInput,
                root: size === 'large' ? classes.bigInputRoot : null,
              },
            }}
          />
        );
      }}
      renderOption={(props, option, state) => {
        return (
          <RenderSuggestion
            suggestion={option}
            itemProps={props}
            inputValue={state.inputValue}
            index={state.index}
          />
        );
      }}
    />
  );
};

export default Autocomplete;
